import { useDeferredValue, useState } from 'react'
import { format, parseISO } from 'date-fns'
import { Sparkles, RefreshCw, Loader2, CheckCircle2, MinusCircle, XCircle, FileText, PenLine, Coins, Laptop, Brain } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { SegmentedControl, type SegmentOption } from '@/components/ui/segmented-control'
import { cn } from '@/lib/utils'
import { useSaveFeedback } from '@/hooks/useAiSummaries'
import { useCoaching, useCoachingEstimate, type CoachingCostEstimate } from '@/hooks/useCoaching'
import { useAppSettings, useUpdateSettings } from '@/hooks/useAppSettings'
import { useLLMProvider } from '@/hooks/useLLMProvider'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { streamingParagraphs } from '@/lib/coachingBody'
import { modelLabel } from '@/lib/modelLabel'
import { centralMonthName, costDeltaPercent, creditLeft, formatCentralTime, monthTotals, thinkingWords } from '@/lib/letterMeta'
import { LetterBody } from './LetterBody'
import { ThinkingPanel } from './ThinkingPanel'
import { CoachingHistory } from './CoachingHistory'
import { DataPostureView } from './DataPostureView'
import type { CoachingMode } from '@/types/coaching'
import type { AiSummaryBody, FeedbackRating } from '@/types'

// ─── Mode config ──────────────────────────────────────────────────────────────

const MODES: { value: CoachingMode; label: string; description: string }[] = [
  {
    value: 'integrated',
    label: 'Integrated',
    description: 'Sees faith, work, family, and health as one interconnected system.',
  },
  {
    value: 'biblical_discernment',
    label: 'Discernment',
    description: 'Draws on Scripture, calling, stewardship, and the wisdom tradition.',
  },
  {
    value: 'systems_thinker',
    label: 'Systems',
    description: 'Analyzes allocation, leverage points, and resource alignment precisely.',
  },
  {
    value: 'trusted_friend',
    label: 'Friend',
    description: 'Warm, honest, direct — speaks truth with empathy and care.',
  },
]

const MODE_LABELS = Object.fromEntries(MODES.map(m => [m.value, m.label]))

// Haptic taps when a model is chosen on iPhone: more taps for a bigger model.
const MODEL_BUZZES: Record<string, number> = { haiku: 1, sonnet: 2, opus: 3, fable: 4 }
function modelBuzzes(id: string): number {
  return MODEL_BUZZES[/^claude-([a-z]+)/.exec(id)?.[1] ?? ''] ?? 1
}

// ─── Feedback section ─────────────────────────────────────────────────────────

const RATING_OPTIONS: {
  value: FeedbackRating
  label: string
  Icon: React.ElementType
  active: string
  inactive: string
}[] = [
  {
    value: 'landed',
    label: 'Landed',
    Icon: CheckCircle2,
    active: 'bg-emerald-100 border-emerald-400 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-600 dark:text-emerald-300',
    inactive: 'border-border text-muted-foreground hover:text-foreground hover:border-ring',
  },
  {
    value: 'mixed',
    label: 'Mixed',
    Icon: MinusCircle,
    active: 'bg-amber-100 border-amber-400 text-amber-700 dark:bg-amber-950/40 dark:border-amber-600 dark:text-amber-300',
    inactive: 'border-border text-muted-foreground hover:text-foreground hover:border-ring',
  },
  {
    value: 'missed',
    label: 'Missed',
    Icon: XCircle,
    active: 'bg-red-100 border-red-400 text-red-700 dark:bg-red-950/40 dark:border-red-600 dark:text-red-300',
    inactive: 'border-border text-muted-foreground hover:text-foreground hover:border-ring',
  },
]

function FeedbackSection({
  summaryId,
  initialRating,
  initialNote,
}: {
  summaryId: string
  initialRating?: FeedbackRating
  initialNote?: string
}) {
  const [rating, setRating] = useState<FeedbackRating | undefined>(initialRating)
  const [note, setNote] = useState(initialNote ?? '')
  const [saved, setSaved] = useState(!!initialRating)
  const [saving, setSaving] = useState(false)
  const saveFeedback = useSaveFeedback()

  function handleRating(r: FeedbackRating) {
    setRating(r)
    setSaved(false)
  }

  async function handleSave() {
    if (!rating) return
    setSaving(true)
    try {
      await saveFeedback.mutateAsync({ id: summaryId, rating, note: note.trim() || undefined })
      setSaved(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3 pt-4 border-t border-border">
      <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
        Was this helpful?
      </p>

      <div className="flex gap-2">
        {RATING_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => handleRating(opt.value)}
            className={cn(
              'flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors',
              rating === opt.value ? opt.active : opt.inactive,
            )}
          >
            <opt.Icon size={12} />
            {opt.label}
          </button>
        ))}
      </div>

      {rating && (
        <div className="space-y-2">
          <textarea
            value={note}
            onChange={e => { setNote(e.target.value); setSaved(false) }}
            placeholder="Anything to correct or add for next time? (optional)"
            rows={2}
            className="w-full text-sm px-3 py-2 rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground/60"
          />
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => void handleSave()}
              disabled={saving || saved}
              className="h-7 text-xs"
            >
              {saving ? 'Saving…' : saved ? 'Saved' : 'Save feedback'}
            </Button>
            {saved && (
              <span className="text-xs text-muted-foreground">
                Included in your next letter
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Cost estimate ────────────────────────────────────────────────────────────

function formatUsd(usd: number): string {
  return usd < 0.01 ? '<$0.01' : `$${usd.toFixed(2)}`
}

/** A letter's actual cost: to the tenth of a cent, since letters usually cost a few cents. */
function formatUsdExact(usd: number): string {
  return usd < 0.001 ? '<$0.001' : usd < 1 ? `$${usd.toFixed(3)}` : `$${usd.toFixed(2)}`
}

/** Under a letter: when it was written, how much the coach thought, its cost against the
 *  estimate, its tokens, and this month's running totals. */
function LetterUsage({ createdAt, thinking, costUsd, estimatedCostUsd, inputTokens, outputTokens, allLetters, creditUsd }: {
  createdAt: string
  thinking?: string | null
  costUsd?: number | null
  estimatedCostUsd?: number | null
  inputTokens?: number | null
  outputTokens?: number | null
  /** Every letter, for the month's totals and the credit left. */
  allLetters: { createdAt: string; costUsd?: number | null }[]
  creditUsd?: number | null
}) {
  const words = thinkingWords(thinking)
  const delta = costUsd != null && estimatedCostUsd != null ? costDeltaPercent(costUsd, estimatedCostUsd) : null
  const month = monthTotals(allLetters)
  const left = creditUsd != null ? creditLeft(creditUsd, allLetters) : null

  return (
    <div aria-label="Letter usage" className="space-y-2 border-t border-border pt-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {costUsd != null && (
          <Metric icon={Coins} label="Cost" title="What this letter cost to write, at list price">
            {formatUsdExact(costUsd)}
            {estimatedCostUsd != null && (
              <span className="text-muted-foreground/70">
                {' '}· est. {formatUsdExact(estimatedCostUsd)}
                {delta !== null && delta !== 0 && ` (${delta > 0 ? '+' : ''}${delta}%)`}
              </span>
            )}
          </Metric>
        )}
        {inputTokens != null && (
          <Metric icon={FileText} label="Tokens in" title="Tokens sent: your priorities, areas and the coaching instructions">
            {inputTokens.toLocaleString()} in
          </Metric>
        )}
        {outputTokens != null && (
          <Metric icon={PenLine} label="Tokens out" title="Tokens written, including any thinking">
            {outputTokens.toLocaleString()} out
          </Metric>
        )}
        {words > 0 && (
          <Metric icon={Brain} label="Thinking" title="Words in the coach's summarized thinking before it wrote">
            {words.toLocaleString()} words thought
          </Metric>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Written {formatCentralTime(createdAt)}
        <br />
        {centralMonthName(new Date())}: {month.letters} {month.letters === 1 ? 'letter' : 'letters'} · {formatUsdExact(month.costUsd)}
        {left !== null && (
          <span title="Your prepaid credit minus what Plumb's letters have cost. Approximate: it can't see other use of your API key.">
            {' '}· ≈ {formatUsdExact(left)} of ${creditUsd} credit left
          </span>
        )}
      </p>
    </div>
  )
}

/** One small labeled figure in the cost line. */
function Metric({
  icon: Icon,
  label,
  title,
  children,
}: {
  icon: React.ElementType
  label: string
  title?: string
  children: React.ReactNode
}) {
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] tabular-nums text-muted-foreground"
    >
      <Icon size={11} aria-hidden className="flex-shrink-0" />
      <span className="sr-only">{label}: </span>
      {children}
    </span>
  )
}


function CostEstimate({ estimate }: { estimate: CoachingCostEstimate | undefined }) {
  if (!estimate) return <span className="text-[11px] text-muted-foreground">Estimating cost…</span>
  if (estimate.cached) {
    return (
      <Metric icon={CheckCircle2} label="Cost">
        No changes since your last letter · no charge
      </Metric>
    )
  }
  if (estimate.provider === 'ollama') {
    return <Metric icon={Laptop} label="Cost">Runs locally · no charge</Metric>
  }

  const n = estimate.typicalFromLetters
  const outputBasis = n > 0
    ? `Typical output from your last ${n} letter${n === 1 ? '' : 's'} with this model`
    : 'Typical output for this model; it adjusts to your own letters as you write them'
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Metric
        icon={FileText}
       
        label="Input tokens"
        title={`Tokens sent: your priorities, areas and the coaching instructions${estimate.inputTokensExact ? '' : ' (estimated)'}`}
      >
        {estimate.inputTokensExact ? '' : '~'}{estimate.inputTokens.toLocaleString()} in
      </Metric>
      <Metric icon={PenLine} label="Output tokens" title={outputBasis}>
        ~{estimate.typicalOutputTokens.toLocaleString()} out
      </Metric>
      {estimate.typicalCostUsd !== null && estimate.maxCostUsd !== null && (
        <Metric
          icon={Coins}
         
          label="Cost"
          title={`Typical cost. The most a letter can cost with this model is ${formatUsd(estimate.maxCostUsd)}.`}
        >
          {estimate.typicalCostUsd < 0.01 ? '' : '~'}{formatUsd(estimate.typicalCostUsd)}
          <span className="text-muted-foreground/60">· max {formatUsd(estimate.maxCostUsd)}</span>
        </Metric>
      )}
    </div>
  )
}

// ─── Letter settings ──────────────────────────────────────────────────────────

/** One labeled choice (coaching style, or model): segmented control on phones, pills elsewhere. */
function PillRow<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: SegmentOption<T>[]
  value: T | undefined
  onChange: (value: T) => void
}) {
  const isMobile = useIsMobile()
  // Phones: a full-width iOS-style segmented control. Larger screens: compact pills.
  if (isMobile) {
    return (
      <div className="min-w-0 space-y-2">
        <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
        <SegmentedControl label={label} options={options} value={value} onChange={onChange} />
      </div>
    )
  }
  const description = options.find(o => o.value === value)?.description
  return (
    <div role="radiogroup" aria-label={label} className="min-w-0 space-y-1.5">
      <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
      <div className="space-y-1">
        <div className="flex flex-wrap gap-1">
          {options.map(o => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={value === o.value}
              onClick={() => onChange(o.value)}
              className={cn(
                'text-[11px] px-2 py-0.5 rounded-full border transition-colors',
                value === o.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-ring',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
        {description && <p className="text-[10px] text-muted-foreground leading-snug">{description}</p>}
      </div>
    </div>
  )
}

// ─── Streaming letter ─────────────────────────────────────────────────────────

/** The letter's paragraphs as they're written, read out of the JSON still streaming in. */
function StreamingLetter({ text }: { text: string }) {
  if (!text) return null
  const paragraphs = streamingParagraphs(text)
  if (paragraphs.length === 0) {
    return <p className="text-xs text-muted-foreground">Starting the letter…</p>
  }
  return (
    <div className="space-y-4" aria-live="polite">
      {paragraphs.map((para, i) => (
        <p key={i} className="text-sm leading-relaxed text-foreground/90">
          {para}
          {i === paragraphs.length - 1 && (
            <span className="animate-pulse motion-reduce:animate-none text-muted-foreground">▍</span>
          )}
        </p>
      ))}
    </div>
  )
}

// ─── Panel ────────────────────────────────────────────────────────────────────

type PanelTab = 'letter' | 'history' | 'privacy'

export function CoachingPanel() {
  const coaching = useCoaching()
  const {
    context,
    summaries,
    journalExcludedCount,
    generate,
    isGenerating,
    live,
    result,
    error,
    clearResult,
  } = coaching
  const llm = useLLMProvider()

  const settings = useAppSettings()
  const updateSettings = useUpdateSettings()
  const mode: CoachingMode = (settings?.preferredCoachingMode as CoachingMode | undefined) ?? 'integrated'
  const [resultDate, setResultDate] = useState<string | null>(null)
  const [tab, setTab] = useState<PanelTab>('letter')
  const [includeFullJournal, setIncludeFullJournal] = useState(false)
  // Open while a letter is being written, collapsed once it's done (and for saved letters).
  const [thinkingOpen, setThinkingOpen] = useState(false)
  // The saved choice if the server still offers it, else the server's default model.
  const models = llm?.models ?? []
  const preferredModel = settings?.preferredCoachingModel
  const model = llm?.provider === 'anthropic'
    ? (models.some(m => m.id === preferredModel) ? preferredModel! : llm.model)
    : undefined
  const selectedModel = models.find(m => m.id === model)
  const modelThinks = selectedModel?.thinks ?? llm?.thinks ?? false
  // Pricing a new choice is background work: it runs at low priority, after the controls update.
  const deferredMode = useDeferredValue(mode)
  const deferredModel = useDeferredValue(model)
  const { estimate, updating: estimateUpdating } = useCoachingEstimate(coaching, deferredMode, includeFullJournal, deferredModel)

  function handleModeChange(m: CoachingMode) {
    updateSettings.mutate({ preferredCoachingMode: m })
  }

  function handleModelChange(id: string) {
    updateSettings.mutate({ preferredCoachingModel: id })
  }

  // A just-written letter gets its saved record (with the cost) once it reloads in the background.
  const savedResult = result ? summaries.find(s => s.snapshotId === result.snapshotId) : undefined
  const activeLetter = result
    ? {
        body:                   result.body as AiSummaryBody,
        distressFloorTriggered: result.distressFloorTriggered,
        modeLabel:              MODE_LABELS[mode] ?? mode,
        model:                  result.model ?? null,
        createdAt:              resultDate ?? new Date().toISOString(),
        cached:                 result.cached,
        thinking:               result.thinking ?? null,
        inputTokens:            result.inputTokens ?? savedResult?.inputTokens ?? null,
        outputTokens:           result.outputTokens ?? savedResult?.outputTokens ?? null,
        costUsd:                savedResult?.costUsd ?? null,
        estimatedCostUsd:       savedResult?.estimatedCostUsd ?? null,
        summaryId:              summaries[0]?.id,
        initialRating:          summaries[0]?.feedbackRating,
        initialNote:            summaries[0]?.feedbackNote,
      }
    : summaries[0]
      ? {
          body:                   summaries[0].body,
          distressFloorTriggered: summaries[0].distressFloorTriggered,
          modeLabel:              MODE_LABELS[summaries[0].coachingMode] ?? summaries[0].coachingMode,
          model:                  summaries[0].model ?? null,
          createdAt:              summaries[0].createdAt,
          cached:                 true,
          thinking:               summaries[0].thinking ?? null,
          inputTokens:            summaries[0].inputTokens ?? null,
          outputTokens:           summaries[0].outputTokens ?? null,
          costUsd:                summaries[0].costUsd ?? null,
          estimatedCostUsd:       summaries[0].estimatedCostUsd ?? null,
          summaryId:              summaries[0].id,
          initialRating:          summaries[0].feedbackRating,
          initialNote:            summaries[0].feedbackNote,
        }
      : null

  async function handleGenerate() {
    setResultDate(null)
    clearResult()
    setTab('letter')
    setThinkingOpen(true)
    try {
      // Save the estimate that was on screen, unless it was still catching up with a new choice.
      const shownEstimate = estimate && !estimate.cached && !estimateUpdating && estimate.provider !== 'ollama'
        ? estimate.typicalCostUsd ?? undefined
        : undefined
      await generate(mode, includeFullJournal, model, shownEstimate)
      setResultDate(new Date().toISOString())
      setThinkingOpen(false)
    } catch {
      // error surfaced by hook
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto">
      <div className="w-full space-y-5 pb-10 px-4 md:px-8">

        {/* Header: title, with the view toggle across from it */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold">AI coaching letter</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {context
                ? `${context.priorities.length} active priorities · ${Math.round(context.committedHoursPerWeek)}h/wk committed`
                : 'Loading your context…'}
            </p>
          </div>

          <div role="tablist" aria-label="Coaching views" className="flex gap-0.5 p-0.5 rounded-md bg-muted">
            {(
              [
                ['letter', 'Letter'],
                ['history', `History${summaries.length > 0 ? ` (${summaries.length})` : ''}`],
                ['privacy', 'Privacy'],
              ] as [PanelTab, string][]
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={tab === value}
                onClick={() => setTab(value)}
                className={cn(
                  'text-xs px-3 py-1 rounded transition-colors',
                  tab === value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Provider status: only when there's something to fix, or letters run locally */}
        {llm && (!llm.ready || llm.provider === 'ollama') && (
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border',
                llm.ready
                  ? 'border-border text-muted-foreground'
                  : 'border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-950/20 dark:text-amber-300',
              )}
            >
              <span
                className={cn(
                  'w-1.5 h-1.5 rounded-full flex-shrink-0',
                  llm.ready ? 'bg-emerald-500' : 'bg-amber-400',
                )}
              />
              {llm.model}
              <span className="text-muted-foreground/60">
                {llm.provider === 'ollama' ? '· local' : '· cloud'}
              </span>
            </span>
            {!llm.ready && (
              <span className="text-xs text-amber-700 dark:text-amber-300">
                {llm.provider === 'anthropic'
                  ? 'Add ANTHROPIC_API_KEY to .dev.vars, or run wrangler secret put on the live server'
                  : 'Ollama not reachable'}
              </span>
            )}
          </div>
        )}

        {/* Letter settings: style, model and journal sharing, then the cost and Generate */}
        {tab !== 'privacy' && (
          <section aria-label="Letter settings" className="rounded-lg border border-border">
            <div className="px-3 py-3 space-y-4">
              <div className="grid gap-4 lg:grid-cols-2">
                <PillRow
                  label="Coaching style"
                  options={MODES}
                  value={mode}
                  onChange={handleModeChange}
                />
                {models.length > 0 && (
                  <PillRow
                    label="Model"
                    options={models.map(m => ({
                      value: m.id,
                      label: modelLabel(m.id),
                      description: m.blurb,
                      buzzes: modelBuzzes(m.id),
                    }))}
                    value={model}
                    onChange={handleModelChange}
                  />
                )}
              </div>
              {/* FR-AI12 — Opt-in to include full journal text */}
              {(context?.journalThemes?.length ?? 0) > 0 && (
                <div className="space-y-1.5">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Journal</span>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="include-full-journal"
                      checked={includeFullJournal}
                      onCheckedChange={setIncludeFullJournal}
                      className="scale-75 origin-left"
                    />
                    <Label htmlFor="include-full-journal" className="text-[11px] text-muted-foreground cursor-pointer leading-snug">
                      Share full journal text with coach
                    </Label>
                  </div>
                </div>
              )}
            </div>

            {/* The panel shares the row with the entry list, so it only gets wide enough for one line at lg */}
            <div className="flex flex-col gap-2.5 border-t border-border bg-muted/30 px-3 py-2.5 lg:flex-row lg:items-center lg:justify-between">
              <div className={cn('transition-opacity duration-150', estimateUpdating && 'opacity-50')} aria-busy={estimateUpdating}>
                <CostEstimate estimate={estimate} />
              </div>
              <Button
                size="sm"
                onClick={() => void handleGenerate()}
                disabled={isGenerating || !context}
                className="gap-1.5 w-full lg:w-auto lg:ml-auto flex-shrink-0"
              >
                {isGenerating ? (
                  <><Loader2 size={13} className="animate-spin" /> Generating…</>
                ) : activeLetter ? (
                  <><RefreshCw size={13} /> Generate again</>
                ) : (
                  <><Sparkles size={13} /> Generate letter</>
                )}
              </Button>
            </div>
          </section>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2.5">
            <p className="text-xs text-destructive font-medium">{error}</p>
          </div>
        )}

        {/* ── History tab ── */}
        {tab === 'history' && (
          <CoachingHistory summaries={summaries} />
        )}

        {/* ── Privacy tab ── */}
        {tab === 'privacy' && (
          <DataPostureView
            context={context}
            llm={llm}
            excludedCount={journalExcludedCount}
          />
        )}

        {/* ── Letter tab ── */}
        {tab === 'letter' && (
          <>
            {/* Generating: summarized thinking streams first, then the letter beneath it */}
            {isGenerating && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 size={13} className="animate-spin" />
                  Generating your letter…
                </div>
                {/* A model may skip thinking; once the letter starts without any, drop the panel. */}
                {live && modelThinks && (live.thinking || !live.text) && (
                  <ThinkingPanel
                    text={live.thinking}
                    open={thinkingOpen}
                    onOpenChange={setThinkingOpen}
                    streaming={!live.text}
                  />
                )}
                {live && model && !modelThinks && (
                  <p className="text-[11px] text-muted-foreground">{modelLabel(model)} writes without a thinking step.</p>
                )}
                <StreamingLetter text={live?.text ?? ''} />
              </div>
            )}

            {/* Letter */}
            {!isGenerating && activeLetter && (
              <div className="space-y-5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground border-b border-border pb-3">
                  <span className="font-medium text-foreground">
                    {format(parseISO(activeLetter.createdAt), 'MMMM d, yyyy')}
                  </span>
                  <span>·</span>
                  <span>{activeLetter.modeLabel}</span>
                  {activeLetter.model && (
                    <>
                      <span>·</span>
                      <span>{modelLabel(activeLetter.model)}</span>
                    </>
                  )}
                  {activeLetter.cached && (
                    <>
                      <span>·</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        cached
                      </span>
                    </>
                  )}
                </div>

                {activeLetter.thinking && (
                  <ThinkingPanel text={activeLetter.thinking} open={thinkingOpen} onOpenChange={setThinkingOpen} />
                )}

                <LetterBody
                  body={activeLetter.body}
                  distressFloorTriggered={activeLetter.distressFloorTriggered}
                />

                <LetterUsage
                  createdAt={activeLetter.createdAt}
                  thinking={activeLetter.thinking}
                  costUsd={activeLetter.costUsd}
                  estimatedCostUsd={activeLetter.estimatedCostUsd}
                  inputTokens={activeLetter.inputTokens}
                  outputTokens={activeLetter.outputTokens}
                  allLetters={summaries}
                  creditUsd={llm?.creditUsd}
                />

                {!activeLetter.distressFloorTriggered && activeLetter.summaryId && (
                  <FeedbackSection
                    key={activeLetter.summaryId}
                    summaryId={activeLetter.summaryId}
                    initialRating={activeLetter.initialRating}
                    initialNote={activeLetter.initialNote}
                  />
                )}
              </div>
            )}

            {/* Empty state */}
            {!isGenerating && !activeLetter && !error && (
              <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
                <Sparkles size={28} className="opacity-20" />
                <p className="text-sm">Generate your first coaching letter.</p>
                <p className="text-xs">
                  The coach reads your priorities, allocation, and recent journal themes.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
